import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { TimesEnum } from '@shared/enums';
import { StorageKey } from '@shared/storage';
import { UserStore } from '@shared/store';

import { useLDClient } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { useInterval, useLocalStorage } from 'react-use';
import { AppFeature, useIsFeatureEnabled } from 'src/config';
import { useCurrentPlatform } from 'src/lib/utils/hooks/useCurrentPlatform';

import { useFetchPlannedMaintenance } from './maintenance/useFetchPlannedMaintenance';

const useSwyftxMargin = () => {
  const { userProfile } = UserStore.useUserStore;
  const { isFeatureEnabled } = useIsFeatureEnabled();
  const location = useLocation();
  const LDClient = useLDClient();
  const { currentPlatform, checkCurrentPlatform } = useCurrentPlatform();
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);
  const [lastDismissedMaintenance, setLastDismissedMaintenance] = useLocalStorage<number>(
    StorageKey.LAST_DISMISS_MAINTENANCE,
    -1,
  );

  useEffect(() => {
    // If the LDClient hasn't been identified with the user uuid key don't process the platform yet
    const ldKey = LDClient?.getContext().key;
    if (!userProfile || !ldKey || ldKey === 'swyftx') return;

    checkCurrentPlatform(userProfile);
  }, [LDClient, checkCurrentPlatform, userProfile]);

  const swyftxMarginEnabled = useMemo(
    () => userProfile?.derivativesEnabled && isFeatureEnabled(AppFeature.SwyftxDerivatives),
    [isFeatureEnabled, userProfile?.derivativesEnabled],
  );

  const { data: plannedMaintenance } = useFetchPlannedMaintenance({ enabled: swyftxMarginEnabled });

  const isSwyftxMargin = useMemo(
    () => swyftxMarginEnabled && currentPlatform === 'derivatives',
    [swyftxMarginEnabled, currentPlatform],
  );

  const isMarginRoute = useMemo(() => location.pathname.includes('derivatives'), [location.pathname]);

  useInterval(() => {
    if (!swyftxMarginEnabled || !plannedMaintenance || plannedMaintenance.actualEndTime !== null) {
      setIsUnderMaintenance(false);
      return;
    }

    const now = DateTime.now();
    const startTime = DateTime.fromMillis(plannedMaintenance.startTime);

    if (now >= startTime) {
      setIsUnderMaintenance(true);
      return;
    }

    const delay = startTime.diff(now).toMillis();

    const timer = setTimeout(() => {
      setIsUnderMaintenance(true);
    }, delay);

    return () => clearTimeout(timer);
  }, TimesEnum.SECOND);

  const showDismissedMaintenanceBanner = useMemo(() => {
    if (!lastDismissedMaintenance) return true;

    const now = DateTime.now();
    if (DateTime.fromMillis(lastDismissedMaintenance).plus({ days: 1 }) < now) return true;

    return false;
  }, [lastDismissedMaintenance]);

  return {
    isUnderMaintenance,
    showDismissedMaintenanceBanner,
    setLastDismissedMaintenance,
    swyftxMarginEnabled,
    plannedMaintenance,
    isSwyftxMargin,
    isMarginRoute,
  };
};

export { useSwyftxMargin };
